import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Api from "../../Api";
import { TextBlock, PasswordBlock, CheckboxItem } from "../../parts/FormBlocks";
import Section from "../../parts/Section";
import DiscreetMessage from "../../parts/DiscreetMessage";
import { UIValue, User } from "../../types";

const UsersUpdate = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);

  const updateUser = (key: string) => (value: UIValue) => {
    setUser((user) => ({ ...user!, [key]: value }));
  };

  const updatePermissions = (key: string) => (checked: boolean) => {
    setUser((user) => ({
      ...user!,
      permissions: {
        ...user!.permissions,
        [key]: checked,
      },
    }));
  };

  const updateConfigs = (key: string) => (checked: boolean) => {
    setUser((user) => ({
      ...user!,
      configs: { ...user!.configs, [key]: checked },
    }));
  };

  useEffect(() => {
    Api.usersGet(uuid!).then(setUser);
  }, [uuid]);

  const save = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    const { success } = await Api.usersUpdate(uuid!, user!, password ?? "");
    success ? navigate("/usuarios") : setError(true);
    setSaving(false);
  };

  if (!user) return <DiscreetMessage text="Carregando ..." />;

  return (
    <Section className="p-4">
      <form className="flex flex-col gap-4 pb-32" onSubmit={save}>
        <TextBlock
          label={"Nome"}
          value={user.firstName}
          onChange={updateUser("firstName")}
        />

        <TextBlock
          label={"Sobrenome"}
          value={user.lastName}
          onChange={updateUser("lastName")}
        />

        <TextBlock
          label={"Usuário"}
          value={user.username}
          onChange={updateUser("username")}
        />

        {password === null && (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-bold flex items-center">
              <span className="flex-1">Senha</span>
              <span
                className="text-xs text-gray-600 hover:text-sky-600 cursor-pointer"
                onClick={() => setPassword("")}
              >
                Alterar <FontAwesomeIcon icon="pen-to-square" />
              </span>
            </label>
            <input
              className="border rounded py-2 px-3"
              type="password"
              value="******"
              disabled
            />
          </div>
        )}

        {password !== null && (
          <PasswordBlock
            label={"Senha"}
            value={password}
            onChange={setPassword}
          />
        )}

        <div className="flex flex-col gap-1">
          <div className="text-sm font-bold">Ativo</div>
          <div className="flex text-sm font-bold">
            <label className="flex gap-3 border py-1 px-3 hover:bg-slate-100 cursor-pointer rounded-l-full border-r-0">
              <input
                type="radio"
                checked={user.active}
                onChange={() => updateUser("active")(true)}
                value="active"
              />{" "}
              <span>Sim</span>
            </label>

            <label className="flex gap-3 border py-1 px-3 hover:bg-slate-100 cursor-pointer rounded-r-full">
              <input
                type="radio"
                checked={!user.active}
                onChange={() => updateUser("active")(false)}
                value="inactive"
              />{" "}
              <span>Não</span>
            </label>
          </div>
        </div>

        <div className="flex flex-col gap-2 text-sm">
          <label className="font-bold">Permissões</label>

          <CheckboxItem
            label="Clientes"
            checked={user.permissions.registrations}
            onChange={updatePermissions("registrations")}
          />

          <CheckboxItem
            label="Financeiro"
            checked={user.permissions.receipts}
            onChange={updatePermissions("receipts")}
          />

          <CheckboxItem
            label="Meus orçamentos"
            checked={user.permissions.myQuotes}
            onChange={updatePermissions("myQuotes")}
          />

          <CheckboxItem
            label="Aprovar orçamentos"
            checked={user.permissions.quotesAdmin}
            onChange={updatePermissions("quotesAdmin")}
          />

          <CheckboxItem
            label="Autorizações"
            checked={user.permissions.authorizations}
            onChange={updatePermissions("authorizations")}
          />

          <CheckboxItem
            label="Contratos"
            checked={user.permissions.contracts}
            onChange={updatePermissions("contracts")}
          />

          <CheckboxItem
            label="Instalações"
            checked={user.permissions.installations}
            onChange={updatePermissions("installations")}
          />

          <CheckboxItem
            label="Técnico"
            checked={user.permissions.technician}
            onChange={updatePermissions("technician")}
          />

          <CheckboxItem
            label="Ordens de serviço"
            checked={user.permissions.orders}
            onChange={updatePermissions("orders")}
          />

          <CheckboxItem
            label="Estatísticas"
            checked={user.permissions.stats}
            onChange={updatePermissions("stats")}
          />

          <CheckboxItem
            label="Equipamentos"
            checked={user.permissions.equipments}
            onChange={updatePermissions("equipments")}
          />

          <CheckboxItem
            label="Usuários"
            checked={user.permissions.users}
            onChange={updatePermissions("users")}
          />
        </div>

        <div className="flex flex-col gap-2 text-sm">
          <label className="font-bold">Configurações</label>

          <CheckboxItem
            label="Recebe comissão"
            checked={user.configs.commission}
            onChange={updateConfigs("commission")}
          />
        </div>

        <hr />

        {error && (
          <div className="text-sm text-red-700 font-bold">
            Erro ao salvar usuário
          </div>
        )}

        <div className="text-right">
          <button
            className="rounded py-2 px-4 bg-sky-600 hover:bg-sky-500 font-extrabold text-white"
            disabled={saving}
          >
            Salvar
          </button>
        </div>
      </form>
    </Section>
  );
};

export default UsersUpdate;
